import * as React from 'react';
import {
    Edit,
    NumberInput,
    TextInput,
    TabbedForm,
    FormTab,
    SaveButton,
    Toolbar,
    DateInput,
    TextField,
    BooleanField,
    useListContext,
    FunctionField,
    ArrayField,
    Labeled,
    ReferenceField,
    DateField,
    ReferenceManyField,
    SimpleFormIterator,
    SelectInput,
    ReferenceInput,
    RecordContext,
} from 'react-admin';
import { ReferenceOneInput } from '@react-admin/ra-relationships';
import {
    required,
    minValue,
    maxValue,
    minLength,
    maxLength,
    number,
    email,
    regex,
    choices,
    useRefresh,
    useRecordContext,
    useResourceContext,
    Datagrid,
    useStoreContext,
    useEditContext,
    useTranslate,
} from 'react-admin';
import { Grid, Box, Typography, Stack } from '@mui/material';

import {
    CustomRadioButtonGroupInput,
    CustomBulkDeleteButton,
    CustomUserLicenseList,
    CustomLogEventList,
    CustomCheckboxGroupInput,
    CustomSubscriptionList,
    CustomUserList,
    CustomProductList,
    CustomAddSubscriptionButton,
    CustomGroupList,
    CustomSelectInput,
} from '../controls';

import user from '../user';

import {
    LibraryBooks,
    Article,
    Podcasts,
    Attachment,
    DataObject,
    History,
    Person,
    ReceiptLong,
    TransferWithinAStation,
    Diversity1,
    Subscriptions,
    School,
    Face,
    Face5,
    Settings,
} from '@mui/icons-material';
import CreateUserDialogButton from '../user/CreateUserDialogButton';
import { ReferenceManyInput } from '@react-admin/ra-relationships';
import GroupSettings from './GroupSettings';

const EditToolbar = () => {
    return (
        <Toolbar>
            <SaveButton />
        </Toolbar>
    );
};

const SchoolSelect = () => {
    const { record } = useEditContext();
    return (
        <ReferenceInput
            reference="school"
            source="parent_group[0].guid"
            filter={{
                status: '0',
                same_code: record?.parent_group[0].guid || '',
            }}
        >
            <SelectInput
                validate={required()}
                label={false}
                fullWidth
                sx={{ width: '100%' }}
            />
        </ReferenceInput>
    );
};

const GroupEdit = () => {
    const translate = useTranslate();
    return (
        <Edit redirect={false} mutationMode="optimistic">
            <TabbedForm toolbar={<EditToolbar />}>
                {/* Tähän perustiedot */}
                <FormTab
                    label="resources.group.tabs.details"
                    icon={<Article />}
                >
                    <Grid container width={{ xs: '100%' }} spacing={2}>
                        <Grid size={12}>
                            <Box
                                display={{ xs: 'block', sm: 'flex' }}
                                sx={{ marginBottom: '10px' }}
                            >
                                <Labeled
                                    label={translate('resources.school.label')}
                                >
                                    <SchoolSelect />
                                </Labeled>
                            </Box>

                            

                            <Box
                                display={{ xs: 'block', sm: 'flex' }}
                                sx={{ marginBottom: '10px' }}
                            >
                                <Labeled
                                    label={translate(
                                        'resources.group.fields.name'
                                    )}
                                >
                                    <FunctionField
                                        render={record =>
                                            record.public.archived &&
                                            record.public.archived === true
                                                ? record.name + ' (Arkistoitu)'
                                                : record.name
                                        }
                                    />
                                </Labeled>
                            </Box>
                            <Box
                                display={{ xs: 'block', sm: 'flex' }}
                                sx={{ marginBottom: '10px' }}
                            >
                                <Labeled>
                                    <TextField
                                        resource="group"
                                        source="public.code"
                                    />
                                </Labeled>
                            </Box>
                            <Box
                                display={{ xs: 'block', sm: 'flex' }}
                                sx={{ marginBottom: '10px' }}
                            >
                                <Labeled
                                    label={translate(
                                        'resources.group.fields.public.group_type'
                                    )}
                                >
                                    <FunctionField
                                        render={record =>
                                            record.public.group_type ==
                                            'comprehensiveschool'
                                                ? 'Perusopetus'
                                                : record.public.group_type ==
                                                  'highschool16'
                                                ? 'LOPS (2016)'
                                                : record.public.group_type ==
                                                  'highschool21'
                                                ? 'LOPS (2021)'
                                                : record.public.group_type ==
                                                  'flschool'
                                                ? 'Finnlectura'
                                                : ''
                                        }
                                    />
                                </Labeled>
                            </Box>
                        </Grid>

                        <Grid size={12}>
                            <Typography variant="subtitle2" paragraph>
                                Materiaalit
                            </Typography>
                            <ArrayField
                                resource="group"
                                source="public.materials"
                            >
                                <Datagrid
                                    bulkActionButtons={false}
                                    style={{ marginBottom: '30px' }}
                                >
                                    <TextField
                                        source="sarja"
                                        sortable={false}
                                    />
                                    <TextField
                                        source="kirja"
                                        sortable={false}
                                    />
                                    <TextField source="isbn" sortable={false} />
                                    <BooleanField
                                        source="jaettava"
                                        sortable={false}
                                    />
                                </Datagrid>
                            </ArrayField>
                        </Grid>

                        <Grid size={12}>
                            <Stack direction="row" spacing={4}>
                                <Box>
                                    <Labeled label="resources.pupil.fields.author.creator">
                                        <>
                                            <ReferenceField
                                                reference="user"
                                                source="author.creator"
                                            >
                                                <FunctionField
                                                    render={record =>
                                                        `${record.public.firstname} ${record.public.lastname}`
                                                    }
                                                />
                                            </ReferenceField>
                                            <DateField
                                                resource="group"
                                                source="author.created"
                                                showTime
                                                locales={'fi-FI'}
                                            />
                                        </>
                                    </Labeled>
                                </Box>
                                <Box>
                                    <Labeled label="resources.pupil.fields.author.modifier">
                                        <>
                                            <ReferenceField
                                                reference="user"
                                                source="author.modifier"
                                            >
                                                <FunctionField
                                                    render={record =>
                                                        `${record.public.firstname} ${record.public.lastname}`
                                                    }
                                                />
                                            </ReferenceField>
                                            <DateField
                                                resource="group"
                                                source="author.modified"
                                                showTime
                                                locales={'fi-FI'}
                                            />
                                        </>
                                    </Labeled>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                </FormTab>

                <FormTab
                    label="resources.group.tabs.teachers"
                    icon={<School />}
                    path="opettajat"
                >
                    <Grid container width={{ xs: '100%' }} spacing={2}>
                        <Grid size={12}>
                            <CustomUserList
                                disableSyncWithLocation
                                storeKey="groupTeacherList"
                                resource="teacher"
                                filter={{
                                    roletype: '0,1',
                                    status: '0,2,3',
                                    usergroup_status: '0,2,3',
                                    sessions: true,
                                }}
                                customFilters={['group']}
                                showExtra={['search']}
                                hideFields={[
                                    'email',
                                    'role',
                                    'subjects',
                                    'code',
                                    'middlename',
                                    'loginstudent',
                                ]}
                                sort={{
                                    field: 'public.lastname',
                                    order: 'ASC',
                                }}
                                bulkActionButtons={false}
                                emptyIcon={user.iconTeacher}
                            >
                                <span />
                            </CustomUserList>
                        </Grid>
                    </Grid>
                </FormTab>

                <FormTab
                    label="resources.group.tabs.pupils"
                    icon={<Face5 />}
                    path="oppilaat"
                >
                    <Grid container width={{ xs: '100%' }} spacing={2}>
                        <Grid size={12}>
                            <CustomUserList
                                disableSyncWithLocation
                                storeKey="groupPupilList"
                                resource="pupil"
                                filter={{
                                    status: '0',
                                    usergroup_status: '0',
                                }}
                                customFilters={['group']}
                                showExtra={['search']}
                                hideFields={[
                                    'loginteacher',
                                    'subjects',
                                    'role',
                                    'loginstudent',
                                    'email',
                                    'username',
                                ]}
                                sort={{
                                    field: 'public.lastname',
                                    order: 'ASC',
                                }}
                                bulkActionButtons={false}
                                emptyIcon={user.iconPupil}
                            >
                                <span />
                            </CustomUserList>
                        </Grid>
                    </Grid>
                </FormTab>

                <FormTab
                    label="resources.group.tabs.students"
                    icon={<Face />}
                    path="opiskelijat"
                >
                    <Grid container width={{ xs: '100%' }} spacing={2}>
                        <Grid size={12}>
                            <CustomUserList
                                disableSyncWithLocation
                                storeKey="groupStudentList"
                                resource="student"
                                filter={{
                                    status: '0',
                                    usergroup_status: '0',
                                }}
                                customFilters={['group']}
                                showExtra={['search']}
                                hideFields={[
                                    'code',
                                    'middlename',
                                    'subjects',
                                    'role',
                                    'loginteacher',
                                ]}
                                sort={{
                                    field: 'public.lastname',
                                    order: 'ASC',
                                }}
                                bulkActionButtons={false}
                                emptyIcon={user.iconStudent}
                            >
                                <span />
                            </CustomUserList>
                        </Grid>
                    </Grid>
                </FormTab>

                {/* Settings tab disabled for now - maybe in future
                <FormTab
                    label="resources.group.tabs.settings"
                    icon={<Settings />}
                    path="asetukset"
                >
                    <GroupSettings />
                </FormTab>
                */}
            </TabbedForm>
        </Edit>
    );
};

const redirect: any = () => {
    return window.location.hash.replace('#', '');
};

export default GroupEdit;
